import {
  Box,
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";

import { HotkeyTooltipLabel } from "#app/components/keyboard-shortcuts/hotkey-tooltip-label";
import {
  useCollapseSidebar,
  useSidebarCollapsed,
} from "#app/lib/hooks/use-sidebar-collapsed";

// Bare `[`, no modifier — `useSidebarToggleHotkey` ignores the chord while
// the user is typing in an editor, so the plain key is safe to claim.
export const SIDEBAR_TOGGLE_SHORTCUT = ["["];

/**
 * Tooltip body naming a sidebar toggle affordance beside the `[` keycap —
 * one hotkey serves both directions, so this control's "Collapse sidebar"
 * and the module rail's "Open sidebar" share it.
 */
export function SidebarToggleTooltipLabel({ label }: { label: string }) {
  return <HotkeyTooltipLabel keys={SIDEBAR_TOGGLE_SHORTCUT} label={label} />;
}

/**
 * The hover-revealed chevrons-left control on a sidebar pane header — shared
 * by the module menus and the settings sidebar. Collapsing is a desktop-only
 * notion: on mobile the pane lives in a drawer, which dismisses instead of
 * collapsing, so this renders nothing there. It also renders nothing while
 * the pane is ALREADY collapsed — the one place a menu renders in that state
 * is the rail's hover flyout, where "collapse" is a no-op offered on a panel
 * that dismisses by mouse-out.
 */
export function CollapseSidebarButton() {
  const isMobile = useIsMobile();
  const collapsed = useSidebarCollapsed();
  const collapseSidebar = useCollapseSidebar();

  if (isMobile || collapsed) return null;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Box
            hoverReveal="header"
            revealOnTouch
            render={
              <Button
                aria-label="Collapse sidebar"
                onClick={collapseSidebar}
                size="icon-sm"
                variant="ghost"
              />
            }
          >
            <Icon name="chevrons-left" size="sm" />
          </Box>
        }
      />
      <TooltipPopup side="top">
        <SidebarToggleTooltipLabel label="Collapse sidebar" />
      </TooltipPopup>
    </Tooltip>
  );
}
